// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { sessionManager } from "../utils/sessionManager"; // ✅ Import session manager
import { setClientIconUrl } from "../utils/clientIcon";
import { clearProfileCache } from "../hooks/useProfile";

let refreshPromise = null;

const isDev = process.env.NODE_ENV !== 'production';

const AuthContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // ✅ Priority 1: Try to get user from session manager
    const sessionUser = sessionManager.getUser();
    if (sessionUser) {
      return sessionUser;
    }

    // ✅ Priority 2: Check localStorage (persistent)
    const localUser = localStorage.getItem("user");
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch (e) {
        console.error('Error parsing localStorage user:', e);
      }
    }

    // ✅ Priority 3: Check sessionStorage
    const sessionStorageUser = sessionStorage.getItem("user");
    if (sessionStorageUser) {
      try {
        return JSON.parse(sessionStorageUser);
      } catch (e) {
        console.error('Error parsing sessionStorage user:', e);
      }
    }

    // ✅ Priority 4: Fallback - construct from individual localStorage items
    const token = localStorage.getItem('token');
    if (token) {
      return {
        username: localStorage.getItem('employeeName') ||
          localStorage.getItem('employeeEmail')?.split('@')[0] ||
          'User',
        email: localStorage.getItem('employeeEmail'),
        role: localStorage.getItem('role'),
      };
    }

    return null;
  });

  // ✅ Store permissions in app state (not localStorage)
  const [permissions, setPermissions] = useState(null);
  const [workingCompany, setWorkingCompany] = useState(() => {
    return localStorage.getItem("workingCompany") || null;
  });

  // ✅ Sync with session manager when user changes
  useEffect(() => {
    if (!user) {
      // Try to restore user from session manager
      const sessionUser = sessionManager.getUser();
      if (sessionUser) {
        setUser(sessionUser);
      }
    }
  }, [user]);

  // ✅ Resolve permissions when user changes or working company changes
  useEffect(() => {
    // Prefer admin level data attached to `user`, fall back to localStorage `adminLevel`
    let adminLevelData = null;
    if (user && user.adminLevel) {
      adminLevelData = user.adminLevel;
    } else {
      try {
        const raw = localStorage.getItem('adminLevel');
        if (raw) {
          adminLevelData = JSON.parse(raw);
        }
      } catch (e) {
        console.warn('Failed to parse localStorage.adminLevel:', e);
      }
    }

    if (adminLevelData && workingCompany) {
      // Normalize possible shapes from different endpoints
      const resolvedPermissions = {
        adminLevelId: adminLevelData.adminLevelId || adminLevelData.admin_level_id || adminLevelData.id || null,
        adminLevelName: adminLevelData.adminLevelName || adminLevelData.name || adminLevelData.admin_level_name || null,
        clientName: adminLevelData.clientName || adminLevelData.client || null,
        workingCompany: adminLevelData.workingCompany || adminLevelData.working_company || workingCompany,
        modules: adminLevelData.modules || adminLevelData.modules || [],
        features: adminLevelData.features || adminLevelData.features || {},
      };
      setPermissions(resolvedPermissions);
    } else {
      setPermissions(null);
    }
  }, [user, workingCompany]);

  // ✅ Enhanced login function
  const login = (username, rememberMe, additionalData = {}) => {
    const userData = {
      username,
      email: additionalData.email || localStorage.getItem('employeeEmail'),
      role: additionalData.role || localStorage.getItem('role'),
      // ✅ NEW: Include admin level and permissions from JWT
      adminLevel: additionalData.adminLevel || null,
      ...additionalData
    };

    setUser(userData);

    // ✅ Store working company when user logs in
    if (additionalData.workingCompany) {
      localStorage.setItem("workingCompany", additionalData.workingCompany);
      setWorkingCompany(additionalData.workingCompany);
    }

    if (Object.prototype.hasOwnProperty.call(additionalData, "clientIconUrl")) {
      const iconValue = additionalData.clientIconUrl;
      if (iconValue) {
        localStorage.setItem("clientIconUrl", iconValue);
      } else {
        localStorage.removeItem("clientIconUrl");
      }
    }

    // ✅ Initialize session manager with user data
    sessionManager.initData(userData);

    // Store in localStorage or sessionStorage based on rememberMe
    if (rememberMe) {
      localStorage.setItem("user", JSON.stringify(userData));
      sessionStorage.removeItem("user");
    } else {
      sessionStorage.setItem("user", JSON.stringify(userData));
      localStorage.removeItem("user");
    }
  };

  // ✅ Update working company and refresh permissions
  const updateWorkingCompany = (company) => {
    localStorage.setItem("workingCompany", company);
    setWorkingCompany(company);
    // Permissions will auto-resolve based on new workingCompany
  };

  // ✅ Enhanced logout function
  const logout = () => {
    clearProfileCache();
    setUser(null);
    setPermissions(null);
    setWorkingCompany(null);

    // ✅ Clear session manager
    sessionManager.logout();
    setClientIconUrl(null);

    // Clear user data from storage
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    localStorage.removeItem("workingCompany");
  };

  // ✅ Permission checking utilities
  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    return permissions?.modules?.includes(module) || false;
  };

  const hasFeatureAccess = (module, feature) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    if (!hasModuleAccess(module)) return false;
    const moduleFeatures = permissions?.features?.[module] || [];
    return moduleFeatures.includes(feature);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        permissions,
        workingCompany,
        updateWorkingCompany,
        hasModuleAccess,
        hasFeatureAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Refresh access token using the refresh token from cookies
 * Returns new token on success (200), null on failure
 */
export async function refreshToken() {
  // 🔒 If refresh already in progress, wait for it
  if (refreshPromise) {
    if (isDev) console.log('⏳ Waiting for ongoing refresh...');
    return refreshPromise;
  }

  if (isDev) console.log('🔄 Starting token refresh...');

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/users/refresh-token`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.status === 401) {
        if (isDev) console.warn('⚠️ Refresh token invalid');
        performLogout();
        return null;
      }

      if (!response.ok) {
        if (isDev) console.error('❌ Refresh failed:', response.status);
        return null;
      }

      const data = await response.json();
      const newAccessToken = data.token;

      localStorage.setItem('token', newAccessToken);
      if (isDev) console.log('✅ Token refreshed');

      return newAccessToken;
    } catch (err) {
      if (isDev) console.error('❌ Refresh error:', err);
      performLogout();
      return null;
    } finally {
      // 🔓 Release semaphore
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}


/**
 * Perform logout without needing auth context (for use in apiFetch)
 */
function performLogout() {
  clearProfileCache();
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('workingCompany');
  sessionStorage.removeItem('user');
  sessionManager.logout();
  setClientIconUrl(null);

  // Redirect to login
  window.location.href = '/login';
}

/**
 * Fetch API with automatic token refresh on 401
 */
export async function apiFetch(url, options = {}) {
  const accessToken = localStorage.getItem("token");

  // Clone options to avoid mutating caller object
  options = { ...options };

  const isFormData = options.body instanceof FormData;

  // Build headers but don't force Content-Type for FormData (browser will add boundary)
  const headerViewType =
    (options.headers && (options.headers['x-view-type'] || options.headers['X-View-Type'])) ||
    null;
  const resolvedViewType =
    headerViewType ||
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';

  const baseHeaders = {
    ...options.headers,
    'x-view-type': resolvedViewType,
    Authorization: `Bearer ${accessToken}`,
  };
  // Ensure Accept-Language reflects current UI language (defaults to 'en')
  if (!baseHeaders['Accept-Language'] && !baseHeaders['accept-language']) {
    const uiLang = localStorage.getItem('language') || 'en';
    baseHeaders['Accept-Language'] = uiLang;
  }

  if (!isFormData && !baseHeaders["Content-Type"]) {
    baseHeaders["Content-Type"] = "application/json";
  }

  let response = await fetch(url, {
    ...options,
    headers: baseHeaders,
    credentials: "include",
  });

  // If 498 = token expired, try to refresh token and retry
  if (response.status === 498) {
    if (isDev) console.warn('⚠️ Access token expired, attempting refresh...');

    const newAccessToken = await refreshToken();

    // If refresh failed, performLogout() will be called by refreshToken()
    if (!newAccessToken) {
      return response; // Return 401 response
    }

    // Retry the original request with new token
    const retryHeaders = {
      ...options.headers,
      'x-view-type': resolvedViewType,
      Authorization: `Bearer ${newAccessToken}`,
    };

    if (!isFormData && !retryHeaders["Content-Type"]) {
      retryHeaders["Content-Type"] = "application/json";
    }

    response = await fetch(url, {
      ...options,
      headers: retryHeaders,
      credentials: "include",
    });
  }

  return response;
}
